//C:\Users\Anish\xpr_survey\XPRSuvey-App\src\Utility\Alert\ToastContainer.jsx
import React, { useCallback, useEffect,  useRef,useState } from "react";
import { createPortal } from "react-dom";
import { Check, X } from "lucide-react";
import { subscribeToast } from "./toastStore";

const AUTO_DISMISS_MS = 3000;

const STATUS_STYLES = {
    success: {
        container: "border-[#7f35b2]",
        iconBg: "bg-[#7f35b2]",
        iconColor: "text-white",
        barTrack: "bg-gray-100",
        barFill: "bg-[#7f35b2]"
    },
    fail: {
        container: "border-red-500",
        iconBg: "bg-red-500",
        iconColor: "text-white",
        barTrack: "bg-gray-100",
        barFill: "bg-red-500"
    },
};

const ToastItem = ({ toast, onDismiss }) => {
    const styles = STATUS_STYLES[toast.status] || STATUS_STYLES.success;
    const [widthPct, setWidthPct] = useState(100);

    const pausedRef = useRef(false);
    const elapsedRef = useRef(0);
    const dismissedRef = useRef(false);

    const handleDismiss = useCallback(() => {
        if (dismissedRef.current) return;
        dismissedRef.current = true;
        onDismiss(toast.id);
    }, [onDismiss, toast.id]);

    useEffect(() => {
        let rafId;
        let lastTimestamp = performance.now();

        const tick = (now) => {
            if (!pausedRef.current) {
                elapsedRef.current += now - lastTimestamp;
            }
            lastTimestamp = now;

            const remaining = Math.max(AUTO_DISMISS_MS - elapsedRef.current, 0);
            setWidthPct((remaining / AUTO_DISMISS_MS) * 100);

            if (remaining <= 0) {
                handleDismiss();
                return;
            }
            rafId = requestAnimationFrame(tick);
        };

        rafId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafId);
    }, [handleDismiss]);

    const handleMouseEnter = () => {
        pausedRef.current = true;
    };

    const handleMouseLeave = () => {
        pausedRef.current = false;
    };

    return (
        <div
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className={`pointer-events-auto w-[20rem] rounded-xl border-2 bg-white p-2 shadow-sm ${styles.container}`}
        >
            <div className="flex items-center gap-5">

                <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${styles.iconBg}`}>
                    {toast.status === "success" ? (
                        <Check size={15} strokeWidth={3} className={styles.iconColor} />
                    ) : (
                        <X size={15} strokeWidth={3} className={styles.iconColor} />
                    )}
                </div>

                <div className="mt-0.1 flex-1">
                    <p className="text-[14px] font-semibold leading-snug text-slate-800">
                        {toast.message}
                    </p>
                </div>

                <button
                    type="button"
                    onClick={handleDismiss}
                    className="shrink-0 text-slate-400 transition-colors hover:text-slate-600"
                    aria-label="Dismiss"
                >
                    <X size={20} strokeWidth={1.5} />
                </button>
            </div>

            <div className={`mt-2 h-1.5 w-full overflow-hidden rounded-full ${styles.barTrack}`}>
                <div
                    className={`h-full rounded-full ${styles.barFill}`}
                    style={{ width: `${widthPct}%` }}
                />
            </div>
        </div>
    );
};

const ToastContainer = () => {
    const [toasts, setToasts] = useState([]);

    const dismiss = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    useEffect(() => subscribeToast((toast) => setToasts((prev) => [...prev, toast])), []);

    return createPortal(
        <div className="pointer-events-none fixed bottom-4 right-4 z-[10000] flex flex-col gap-3">
            {toasts.map((toast) => (
                <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
            ))}
        </div>,
        document.body
    );
};

export default ToastContainer;
